import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Grid,
  Grow,
  List,
  ListItemButton,
  MenuItem,
  Popper,
  Select
} from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { config, GENDER, SIBLING_TYPE, TWIN_TYPE } from '../../pedigree/config';
import { mintObjectIdHex } from '../../pedigree/saveReconciliation';
import { PEDIGREE } from 'src/components/Constants';
import TextInput from 'src/components/UI/input/TextInput';
import {
  relationship,
  getRelationshipForNewPerson,
  getAllowedRelativeActions,
  RELATIVE_ACTIONS
} from '../../pedigree/utils';
import {
  adoptableChildrenForNewSpouse,
  addSiblingsToRecords,
  addChildrenToRecords
} from '../../pedigree/edgeIntegrity';
import {
  GENDER_IDENTITY,
  spouseRelations
} from 'src/components/questionnaire/Constants';

const getNextId = (dataset) => {
  if (!dataset || dataset.length === 0) return 1;

  const numericIds = dataset.map((p) => {
    if (typeof p.pid === 'number') return p.pid;
    else return 0;
  });

  return Math.max(...numericIds) + 1;
};

export const createPerson = (
  gender,
  user_id,
  dataset = [],
  relationshipValue = ''
) => {
  const pid = getNextId(dataset);
  return {
    user_id,
    pid,
    // Born-stable uid: an ObjectId-shaped hex id becomes the FamilyChanged
    // _id on save, so pins/order/views keyed by this uid never orphan across
    // the save round-trip (see mintObjectIdHex).
    uid: mintObjectIdHex(),
    gender,
    genderIdentity: GENDER_IDENTITY[gender] ?? GENDER_IDENTITY.unknown,
    motherId: null,
    fatherId: null,
    spouseIds: [],
    childrenIds: [],
    siblingIds: [],
    relationship: relationshipValue,
    isDynamicallyAdded: true
  };
};

export default function AddRelativeMenu({
  node,
  user,
  setMemberRecords,
  memberRecords,
  setPedigreeState,
  onCloseMenu,
  sidebar,
  isDisabled = false
}) {
  const hasParents = node.fatherId || node.motherId;
  const hasBothParents = node.fatherId && node.motherId;

  const allowedActions = getAllowedRelativeActions(node);
  const ownerPid =
    node.isProxy && node.proxyOf != null ? node.proxyOf : node.pid;

  const [siblingMenuOpen, setSiblingMenuOpen] = useState(false);
  // State (not a ref) anchor: a ref.current set imperatively doesn't reliably drive Popper
  // positioning (the submenu failed to open); state matches ContextMenu's working level-2 flyout.
  const [siblingAnchorEl, setSiblingAnchorEl] = useState(null);
  const [brotherCount, setBrotherCount] = useState(1);
  const [sisterCount, setSisterCount] = useState(1);
  const [sonCount, setSonCount] = useState(1);
  const [daughterCount, setDaughterCount] = useState(1);

  // Rapid-repeat latch: the structural `disabled` props read the selected node's
  // `data`, which refreshes one-to-two render cycles after an add commits — a
  // fast double-click fires the handler twice before the button greys out. The
  // ref rejects the re-fire synchronously; the state greys the buttons until the
  // burst settles. (The updaters below are ALSO idempotent, so even a click that
  // slips through cannot duplicate structure.)
  const addBusyRef = useRef(false);
  const [addBusy, setAddBusy] = useState(false);
  const guarded =
    (fn) =>
    (...args) => {
      if (addBusyRef.current) return;
      addBusyRef.current = true;
      setAddBusy(true);
      try {
        fn(...args);
        // Signal for the Clinician Copilot guided walkthrough (read-only, no
        // payload beyond the type — nothing person-identifying leaves this
        // module). Any add action advances a walkthrough step whose
        // advanceOn is 'relative-added'. No-op when no tour is running.
        if (typeof window !== 'undefined') {
          window.dispatchEvent(
            new CustomEvent('va:pedigree', {
              detail: { type: 'relative-added' }
            })
          );
        }
      } finally {
        setTimeout(() => {
          addBusyRef.current = false;
          setAddBusy(false);
        }, 400);
      }
    };
  const uiDisabled = isDisabled || addBusy;

  // Which union receives a Son/Daughter when the selected person has more than
  // one spouse (D8): user-selectable, defaulting to the most recent spouse (the
  // historical behaviour). Reset when the selection moves to another person.
  const selfRecord = memberRecords.find((p) => p.pid === ownerPid) || node;
  const spouseIdsForPicker = selfRecord.spouseIds || [];
  const [childCoParent, setChildCoParent] = useState(null);
  useEffect(() => {
    setChildCoParent(null);
  }, [ownerPid]);
  const effectiveChildCoParent =
    childCoParent != null && spouseIdsForPicker.includes(childCoParent)
      ? childCoParent
      : spouseIdsForPicker.length
      ? spouseIdsForPicker[spouseIdsForPicker.length - 1]
      : null;

  const spouseLabel = (spouseId) => {
    const spouse = memberRecords.find((r) => r.pid === spouseId);
    return spouse
      ? `${spouse.firstName || ''} ${spouse.lastName || ''}`.trim() ||
          `UPN ${spouse.upn || spouseId}`
      : `UPN ${spouseId}`;
  };

  const childUnionPicker = (fullWidth) =>
    spouseIdsForPicker.length > 1 && (
      <FormControl
        fullWidth={fullWidth}
        size="small"
        sx={fullWidth ? { px: 1, pt: 1 } : { width: 288 }}
      >
        <FormLabel sx={{ fontSize: '0.75rem', mb: 0.5 }}>
          Child with (partner)
        </FormLabel>
        <Select
          size="small"
          value={effectiveChildCoParent ?? ''}
          disabled={uiDisabled}
          onChange={(e) => setChildCoParent(e.target.value)}
        >
          {spouseIdsForPicker.map((spouseId) => (
            <MenuItem key={spouseId} value={spouseId}>
              {spouseLabel(spouseId)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );

  const openSiblingMenu = (event, gender) => {
    setSiblingAnchorEl(event.currentTarget);
    setSiblingMenuOpen({ open: true, gender });
  };

  const closeSiblingMenu = () => {
    setSiblingMenuOpen({ open: false });
  };

  // Context-menu child adds on a multi-spouse node pick the partner via a NESTED
  // SUBMENU (the same interaction as the Brother/Sister twin submenu). A Select
  // dropdown cannot live inside the context-menu Popper: MUI portals its menu to
  // document.body, so choosing an option registers as a click-away and closes the
  // whole context menu before the selection lands. The sidebar keeps the Select.
  const [childMenuOpen, setChildMenuOpen] = useState({ open: false });
  const [childAnchorEl, setChildAnchorEl] = useState(null);
  const openChildMenu = (event, gender) => {
    setChildAnchorEl(event.currentTarget);
    setChildMenuOpen({ open: true, gender });
  };
  const closeChildMenu = () => {
    setChildMenuOpen({ open: false });
  };

  // Submenu hygiene: hovering ANY item that doesn't own a submenu dismisses the
  // open ones — otherwise a twin/partner submenu lingers after the pointer moves
  // on, and a stray click there applies an option the user had navigated away
  // from.
  const closeSubmenus = () => {
    closeSiblingMenu();
    closeChildMenu();
  };

  // Check if grandparents exist
  const hasGrandparents = () => {
    if (!hasBothParents) return false;

    const father = memberRecords.find((p) => p.pid === node.fatherId);
    const mother = memberRecords.find((p) => p.pid === node.motherId);

    // Check if any parent has parents
    const fatherHasParents = father && (father.fatherId || father.motherId);
    const motherHasParents = mother && (mother.fatherId || mother.motherId);

    return fatherHasParents || motherHasParents;
  };

  const familyLevelMap = new Map(
    memberRecords.map((m) => [m.virtualY, new Set()])
  );
  for (const member of memberRecords) {
    if (familyLevelMap.get(member.virtualY).has(member)) continue;
    else familyLevelMap.get(member.virtualY).add(member);
  }

  const addParents = () => {
    if (node.kind !== config.NODE_TYPE.NODE) return;

    const fatherRel = getRelationshipForNewPerson(
      node,
      user,
      relationship.father
    );
    const motherRel = getRelationshipForNewPerson(
      node,
      user,
      relationship.mother
    );

    // Build the parents INSIDE the updater so their pids come from the LATEST
    // array (no collision with another quick add) and they attach to the real
    // person (ownerPid) even when the selected node is a proxy.
    setMemberRecords((prev) => {
      // Idempotent: a re-fire (double-click) must not create a second couple and
      // orphan the first by overwriting fatherId/motherId.
      const self = prev.find((p) => p.pid === ownerPid);
      if (!self || self.fatherId || self.motherId) return prev;

      const father = createPerson('male', user._id, prev, fatherRel);
      const mother = createPerson(
        'female',
        user._id,
        [...prev, father],
        motherRel
      );

      father.spouseIds.push(mother.pid);
      mother.spouseIds.push(father.pid);
      father.spouseRelationships = {
        [mother.pid]: {
          parentRelationship: spouseRelations.MARRIED,
          isConsanguineous: false
        }
      };
      mother.spouseRelationships = {
        [father.pid]: {
          parentRelationship: spouseRelations.MARRIED,
          isConsanguineous: false
        }
      };
      father.childrenIds.push(ownerPid);
      mother.childrenIds.push(ownerPid);

      return prev
        .map((person) =>
          person.pid === ownerPid
            ? { ...person, fatherId: father.pid, motherId: mother.pid }
            : person
        )
        .concat([father, mother]);
    });
    setPedigreeState((prev) => ({
      ...prev,
      save: PEDIGREE.saveStatus.IN_PROGRESS
    }));

    onCloseMenu();
  };

  const addSpouse = () => {
    if (node.kind !== config.NODE_TYPE.NODE) return;

    const spouseRel = getRelationshipForNewPerson(
      node,
      user,
      relationship.spouse
    );

    setMemberRecords((prev) => {
      // Resolve the selected person + build the spouse against the LATEST array
      // (fresh pid, fresh spouse/child state) — attaches to the real person
      // (ownerPid) even for a proxy.
      const self = prev.find((person) => person.pid === ownerPid);
      if (!self) return prev;

      const spouse = createPerson(
        self.gender === 'male' ? 'female' : 'male',
        user._id,
        prev,
        spouseRel
      );

      // Only existing children whose OTHER parent slot is still empty become
      // children of this new (first) spouse. Children that already have a real
      // other parent must not be re-parented onto the spouse, nor copied into
      // its childrenIds (that would orphan the existing parent's edge — I2).
      const adoptable = new Set(adoptableChildrenForNewSpouse(prev, self));

      const arr = prev.map((person) => {
        // Update the selected node with spouse reference
        if (person.pid === ownerPid) {
          return {
            ...person,
            spouseIds: [...person.spouseIds, spouse.pid],
            spouseRelationships: {
              ...(person.spouseRelationships || {}),
              [spouse.pid]: {
                parentRelationship: spouseRelations.MARRIED,
                isConsanguineous: false
              }
            }
          };
        }

        // Make the spouse the other parent of the node's genuinely-parentless
        // existing children only.
        if (adoptable.has(person.pid)) {
          if (self.gender === 'male') {
            return {
              ...person,
              motherId: spouse.pid
            };
          } else {
            return {
              ...person,
              fatherId: spouse.pid
            };
          }
        }

        return person;
      });

      const newSpouse = {
        ...spouse,
        spouseIds: [ownerPid],
        spouseRelationships: {
          [ownerPid]: {
            parentRelationship: spouseRelations.MARRIED,
            isConsanguineous: false
          }
        },
        childrenIds: [...adoptable]
      };

      const nodeIndex = arr.findIndex((member) => member.pid === ownerPid);

      let insertIndex = nodeIndex + 1;
      (self.spouseIds || []).forEach((existingSpouseId) => {
        const spouseIdx = arr.findIndex((m) => m.pid === existingSpouseId);
        if (spouseIdx !== -1 && spouseIdx + 1 > insertIndex) {
          insertIndex = spouseIdx + 1;
        }
      });
      arr.splice(insertIndex, 0, newSpouse);
      return arr;
    });

    setPedigreeState((prev) => ({
      ...prev,
      save: PEDIGREE.saveStatus.IN_PROGRESS
    }));

    onCloseMenu();
  };

  const addSingleChild = (gender, coParentPid = effectiveChildCoParent) => {
    if (node.kind !== config.NODE_TYPE.NODE) return;
    const relationshipValue = getRelationshipForNewPerson(
      node,
      user,
      gender === 'male' ? relationship.son : relationship.daughter
    );
    // All co-parent/sibling/pid resolution happens against the LATEST records in
    // the pure transform — never a stale closure (rapid-add fix).
    setMemberRecords((prev) =>
      addChildrenToRecords(prev, ownerPid, {
        count: 1,
        coParentPid,
        makeChild: () => createPerson(gender, user._id, [], relationshipValue)
      })
    );
    setPedigreeState((prev) => ({
      ...prev,
      save: PEDIGREE.saveStatus.IN_PROGRESS
    }));
    onCloseMenu();
  };

  const addChild = () => addSingleChild('male');

  const addDaughter = () => addSingleChild('female');

  const addGrandparents = () => {
    if (node.kind !== config.NODE_TYPE.NODE) return;

    setMemberRecords((prev) => {
      // Resolve the selected person from the LATEST array so a quick "Parents
      // then Grandparents" can't read a stale (parentless) node and duplicate
      // the parents. Fresh pids come from the accumulating array below.
      const self = prev.find((p) => p.pid === ownerPid) || node;
      const selfHasBothParents = !!(self.fatherId && self.motherId);

      // Idempotent: if either existing parent already has parents (the same
      // predicate that disables the button), a re-fire must not create a second
      // set of grandparents and re-point the parents at them.
      if (selfHasBothParents) {
        const fatherRec = prev.find((p) => p.pid === self.fatherId);
        const motherRec = prev.find((p) => p.pid === self.motherId);
        if (
          fatherRec?.fatherId ||
          fatherRec?.motherId ||
          motherRec?.fatherId ||
          motherRec?.motherId
        ) {
          return prev;
        }
      }

      const newPeople = [];
      let updatedData = [...prev];
      let fatherId = self.fatherId ?? null;
      let motherId = self.motherId ?? null;

      //Create parents if they don't exist
      if (!selfHasBothParents) {
        if (!fatherId) {
          const father = createPerson(
            'male',
            user._id,
            [...updatedData, ...newPeople],
            getRelationshipForNewPerson(node, user, relationship.father)
          );
          fatherId = father.pid;
          newPeople.push(father);
        }

        if (!motherId) {
          const mother = createPerson(
            'female',
            user._id,
            [...updatedData, ...newPeople],
            getRelationshipForNewPerson(node, user, relationship.mother)
          );
          motherId = mother.pid;
          newPeople.push(mother);
        }

        if (!self.fatherId && !self.motherId) {
          const father = newPeople.find((p) => p.pid === fatherId);
          const mother = newPeople.find((p) => p.pid === motherId);
          if (father && mother) {
            father.spouseIds.push(motherId);
            mother.spouseIds.push(fatherId);
            father.spouseRelationships = {
              [motherId]: {
                parentRelationship: spouseRelations.MARRIED,
                isConsanguineous: false
              }
            };
            mother.spouseRelationships = {
              [fatherId]: {
                parentRelationship: spouseRelations.MARRIED,
                isConsanguineous: false
              }
            };
          }
        }

        // Link parents to child
        const father =
          newPeople.find((p) => p.pid === fatherId) ||
          updatedData.find((p) => p.pid === fatherId);
        const mother =
          newPeople.find((p) => p.pid === motherId) ||
          updatedData.find((p) => p.pid === motherId);

        if (father && !father.childrenIds.includes(ownerPid)) {
          father.childrenIds.push(ownerPid);
        }
        if (mother && !mother.childrenIds.includes(ownerPid)) {
          mother.childrenIds.push(ownerPid);
        }

        updatedData = updatedData.map((person) => {
          if (person.pid === ownerPid) {
            return {
              ...person,
              fatherId: fatherId,
              motherId: motherId
            };
          }
          return person;
        });
      }

      // Add  grandparents : father-side
      const paternalGrandfather = createPerson(
        'male',
        user._id,
        [...updatedData, ...newPeople],
        relationship.fathersFather
      );
      newPeople.push(paternalGrandfather);

      const paternalGrandmother = createPerson(
        'female',
        user._id,
        [...updatedData, ...newPeople],
        relationship.fathersMother
      );
      newPeople.push(paternalGrandmother);

      paternalGrandfather.spouseIds.push(paternalGrandmother.pid);
      paternalGrandmother.spouseIds.push(paternalGrandfather.pid);
      paternalGrandfather.spouseRelationships = {
        [paternalGrandmother.pid]: {
          parentRelationship: spouseRelations.MARRIED,
          isConsanguineous: false
        }
      };
      paternalGrandmother.spouseRelationships = {
        [paternalGrandfather.pid]: {
          parentRelationship: spouseRelations.MARRIED,
          isConsanguineous: false
        }
      };

      paternalGrandfather.childrenIds.push(fatherId);
      paternalGrandmother.childrenIds.push(fatherId);

      // Add  grandparents: mother-side
      const maternalGrandfather = createPerson(
        'male',
        user._id,
        [...updatedData, ...newPeople],
        relationship.mothersFather
      );
      newPeople.push(maternalGrandfather);

      const maternalGrandmother = createPerson(
        'female',
        user._id,
        [...updatedData, ...newPeople],
        relationship.mothersMother
      );
      newPeople.push(maternalGrandmother);

      maternalGrandfather.spouseIds.push(maternalGrandmother.pid);
      maternalGrandmother.spouseIds.push(maternalGrandfather.pid);
      maternalGrandfather.spouseRelationships = {
        [maternalGrandmother.pid]: {
          parentRelationship: spouseRelations.MARRIED,
          isConsanguineous: false
        }
      };
      maternalGrandmother.spouseRelationships = {
        [maternalGrandfather.pid]: {
          parentRelationship: spouseRelations.MARRIED,
          isConsanguineous: false
        }
      };

      maternalGrandfather.childrenIds.push(motherId);
      maternalGrandmother.childrenIds.push(motherId);

      newPeople.forEach((person) => {
        if (person.pid === fatherId) {
          person.fatherId = paternalGrandfather.pid;
          person.motherId = paternalGrandmother.pid;
        }
        if (person.pid === motherId) {
          person.fatherId = maternalGrandfather.pid;
          person.motherId = maternalGrandmother.pid;
        }
      });

      updatedData = updatedData.map((person) => {
        if (person.pid === fatherId) {
          return {
            ...person,
            fatherId: paternalGrandfather.pid,
            motherId: paternalGrandmother.pid
          };
        }
        if (person.pid === motherId) {
          return {
            ...person,
            fatherId: maternalGrandfather.pid,
            motherId: maternalGrandmother.pid
          };
        }
        return person;
      });

      return [...updatedData, ...newPeople];
    });

    setPedigreeState((prev) => ({
      ...prev,
      save: PEDIGREE.saveStatus.IN_PROGRESS
    }));

    onCloseMenu();
  };

  const addSibling = ({ siblingType, gender, twinType }) => {
    if (node.kind !== config.NODE_TYPE.NODE) return;

    const relationshipValue = getRelationshipForNewPerson(
      node,
      user,
      gender === 'male' ? relationship.fullBrother : relationship.fullSister
    );
    const twinSubGroupId = `twin-group-${Math.random()
      .toString(36)
      .slice(2, 8)}`;

    // Build the sibling INSIDE the updater so the selected person, its parents,
    // the sibling group, and the new pid are all read from the LATEST records —
    // never a stale closure. Fixes rapid successive adds landing at the wrong
    // generation / looking like a spouse (the pid collision + stale-parent bug).
    setMemberRecords((prev) =>
      addSiblingsToRecords(prev, ownerPid, {
        count: 1,
        isTwin: siblingType === SIBLING_TYPE.twin,
        siblingType,
        twinType,
        twinSubGroupId,
        makeSibling: () => createPerson(gender, user._id, [], relationshipValue)
      })
    );

    setPedigreeState((prev) => ({
      ...prev,
      save: PEDIGREE.saveStatus.IN_PROGRESS
    }));

    onCloseMenu();
  };

  const addUnknownPerson = () => {
    // Create inside the updater so the pid comes from the LATEST array (no
    // collision with another quick add).
    setMemberRecords((prev) => [
      ...prev,
      createPerson('unknown', user._id, prev, relationship.otherAffected)
    ]);
    setPedigreeState((prev) => ({
      ...prev,
      save: PEDIGREE.saveStatus.IN_PROGRESS
    }));

    onCloseMenu();
  };

  // Add multiple siblings
  const addMultipleSiblings = (gender, count, resetCount) => {
    if (node.kind !== config.NODE_TYPE.NODE) return;
    if (!hasParents || count < 1) return;

    const relationshipValue = getRelationshipForNewPerson(
      node,
      user,
      gender === 'male' ? relationship.fullBrother : relationship.fullSister
    );

    // All sibling creation + edge wiring happens in the pure transform against
    // the LATEST records (collision-free pids, fresh parent refs) — so adding a
    // batch (or several batches in quick succession) can't mis-place them.
    setMemberRecords((prev) =>
      addSiblingsToRecords(prev, ownerPid, {
        count,
        makeSibling: () => createPerson(gender, user._id, [], relationshipValue)
      })
    );

    setPedigreeState((prev) => ({
      ...prev,
      save: PEDIGREE.saveStatus.IN_PROGRESS
    }));

    if (resetCount) {
      resetCount();
    }

    onCloseMenu();
  };

  // Add multiple children (sons or daughters)
  const addMultipleChildren = (gender, count, resetCount) => {
    if (node.kind !== config.NODE_TYPE.NODE) return;
    if (count < 1) return;

    const relationshipValue = getRelationshipForNewPerson(
      node,
      user,
      gender === 'male' ? relationship.son : relationship.daughter
    );

    // Pure transform against the LATEST records: collision-free pids, fresh
    // co-parent + sibship resolution — so a batch (or several quick batches)
    // can't mis-place the children (rapid-add fix).
    setMemberRecords((prev) =>
      addChildrenToRecords(prev, ownerPid, {
        count,
        coParentPid: effectiveChildCoParent,
        makeChild: () => createPerson(gender, user._id, [], relationshipValue)
      })
    );

    setPedigreeState((prev) => ({
      ...prev,
      save: PEDIGREE.saveStatus.IN_PROGRESS
    }));

    if (resetCount) resetCount();
    onCloseMenu();
  };

  return (
    <Box>
      {sidebar ? (
        <Grid
          container
          spacing={2}
          direction="column"
          sx={{
            alignItems: 'flex-end'
          }}
        >
          <Grid>
            <Button
              sx={{ width: 210 }}
              data-va-anchor="pedigree.add-relative.grandparents"
              onClick={guarded(addGrandparents)}
              disabled={
                uiDisabled ||
                hasGrandparents() ||
                !allowedActions.has(RELATIVE_ACTIONS.grandparents)
              }
              variant="contained"
              color="secondary"
            >
              Grandparents
            </Button>
          </Grid>
          <Grid>
            <Button
              sx={{ width: 210 }}
              data-va-anchor="pedigree.add-relative.parents"
              onClick={guarded(addParents)}
              disabled={
                uiDisabled ||
                hasParents ||
                !allowedActions.has(RELATIVE_ACTIONS.parents)
              }
              variant="contained"
              color="secondary"
            >
              Parents
            </Button>
          </Grid>
          <Grid>
            <Button
              sx={{ width: 210 }}
              data-va-anchor="pedigree.add-relative.spouse"
              onClick={guarded(addSpouse)}
              disabled={
                uiDisabled || !allowedActions.has(RELATIVE_ACTIONS.spouse)
              }
              variant="contained"
              color="secondary"
            >
              Spouse
            </Button>
          </Grid>
          <Grid sx={{ width: 'fit-content' }}>
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                alignItems: 'center'
              }}
            >
              <TextInput
                type={'number'}
                placeholder={'1'}
                inputProps={{
                  min: 1,
                  max: 10
                }}
                variant="outlined"
                size="small"
                value={brotherCount}
                onChange={(e) => {
                  const value = parseInt(e.target.value) || 1;
                  setBrotherCount(Math.min(Math.max(value, 1), 10));
                }}
                disabled={
                  uiDisabled ||
                  !hasParents ||
                  !allowedActions.has(RELATIVE_ACTIONS.brother)
                }
                sx={{ width: '70px', flexShrink: 0 }}
              />
              <Button
                sx={{ width: 210, flexShrink: 0 }}
                data-va-anchor="pedigree.add-relative.brother"
                onClick={guarded(() =>
                  addMultipleSiblings(GENDER.male, brotherCount, () =>
                    setBrotherCount(1)
                  )
                )}
                disabled={
                  uiDisabled ||
                  !hasParents ||
                  !allowedActions.has(RELATIVE_ACTIONS.brother)
                }
                variant="contained"
                color="secondary"
              >
                Brother
              </Button>
            </Box>
          </Grid>
          <Grid sx={{ width: 'fit-content' }}>
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                alignItems: 'center'
              }}
            >
              <TextInput
                type={'number'}
                placeholder={'1'}
                inputProps={{
                  min: 1,
                  max: 10
                }}
                variant="outlined"
                size="small"
                value={sisterCount}
                onChange={(e) => {
                  const value = parseInt(e.target.value) || 1;
                  setSisterCount(Math.min(Math.max(value, 1), 10));
                }}
                disabled={
                  uiDisabled ||
                  !hasParents ||
                  !allowedActions.has(RELATIVE_ACTIONS.sister)
                }
                sx={{ width: '70px', flexShrink: 0 }}
              />
              <Button
                sx={{ width: 210, flexShrink: 0 }}
                data-va-anchor="pedigree.add-relative.sister"
                onClick={guarded(() =>
                  addMultipleSiblings(GENDER.female, sisterCount, () =>
                    setSisterCount(1)
                  )
                )}
                disabled={
                  uiDisabled ||
                  !hasParents ||
                  !allowedActions.has(RELATIVE_ACTIONS.sister)
                }
                variant="contained"
                color="secondary"
              >
                Sister
              </Button>
            </Box>
          </Grid>
          {spouseIdsForPicker.length > 1 && (
            <Grid>{childUnionPicker(false)}</Grid>
          )}
          <Grid sx={{ width: 'fit-content' }}>
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                alignItems: 'center'
              }}
            >
              <TextInput
                type={'number'}
                placeholder={'1'}
                inputProps={{
                  min: 1,
                  max: 10
                }}
                variant="outlined"
                size="small"
                value={sonCount}
                onChange={(e) => {
                  const value = parseInt(e.target.value) || 1;
                  setSonCount(Math.min(Math.max(value, 1), 10));
                }}
                disabled={
                  uiDisabled || !allowedActions.has(RELATIVE_ACTIONS.son)
                }
                sx={{ width: '70px', flexShrink: 0 }}
              />
              <Button
                sx={{ width: 210, flexShrink: 0 }}
                data-va-anchor="pedigree.add-relative.son"
                onClick={guarded(() =>
                  addMultipleChildren(GENDER.male, sonCount, () =>
                    setSonCount(1)
                  )
                )}
                disabled={
                  uiDisabled || !allowedActions.has(RELATIVE_ACTIONS.son)
                }
                variant="contained"
                color="secondary"
              >
                Son
              </Button>
            </Box>
          </Grid>
          <Grid>
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                alignItems: 'center'
              }}
            >
              <TextInput
                type={'number'}
                placeholder={'1'}
                inputProps={{
                  min: 1,
                  max: 10
                }}
                variant="outlined"
                size="small"
                value={daughterCount}
                onChange={(e) => {
                  const value = parseInt(e.target.value) || 1;
                  setDaughterCount(Math.min(Math.max(value, 1), 10));
                }}
                disabled={
                  uiDisabled || !allowedActions.has(RELATIVE_ACTIONS.daughter)
                }
                sx={{ width: '70px', flexShrink: 0 }}
              />
              <Button
                sx={{ width: 210, flexShrink: 0 }}
                data-va-anchor="pedigree.add-relative.daughter"
                onClick={guarded(() =>
                  addMultipleChildren(GENDER.female, daughterCount, () =>
                    setDaughterCount(1)
                  )
                )}
                disabled={
                  uiDisabled || !allowedActions.has(RELATIVE_ACTIONS.daughter)
                }
                variant="contained"
                color="secondary"
              >
                Daughter
              </Button>
            </Box>
          </Grid>
          <Grid>
            <Button
              sx={{ width: 210 }}
              onClick={guarded(addUnknownPerson)}
              disabled={
                uiDisabled || !allowedActions.has(RELATIVE_ACTIONS.unknown)
              }
              variant="contained"
              color="secondary"
            >
              Unknown
            </Button>
          </Grid>
        </Grid>
      ) : (
        <>
          <List>
            <ListItemButton
              onMouseEnter={closeSubmenus}
              onClick={guarded(addGrandparents)}
              disabled={
                uiDisabled ||
                hasGrandparents() ||
                !allowedActions.has(RELATIVE_ACTIONS.grandparents)
              }
            >
              Grandparents
            </ListItemButton>
            <ListItemButton
              onMouseEnter={closeSubmenus}
              onClick={guarded(addParents)}
              disabled={
                uiDisabled ||
                hasParents ||
                !allowedActions.has(RELATIVE_ACTIONS.parents)
              }
            >
              Parents
            </ListItemButton>
            <ListItemButton
              onMouseEnter={closeSubmenus}
              onClick={guarded(addSpouse)}
              disabled={
                uiDisabled || !allowedActions.has(RELATIVE_ACTIONS.spouse)
              }
            >
              Spouse
            </ListItemButton>
            <ListItemButton
              onMouseEnter={(e) => {
                closeChildMenu();
                if (hasParents) openSiblingMenu(e, GENDER.male);
              }}
              onClick={(e) => openSiblingMenu(e, GENDER.male)}
              disabled={
                !hasParents || !allowedActions.has(RELATIVE_ACTIONS.brother)
              }
              sx={{ display: 'flex', justifyContent: 'space-between' }}
            >
              Brother
              <ChevronRightIcon fontSize="small" sx={{ ml: 1, opacity: 0.5 }} />
            </ListItemButton>
            <ListItemButton
              onMouseEnter={(e) => {
                closeChildMenu();
                if (hasParents) openSiblingMenu(e, GENDER.female);
              }}
              onClick={(e) => openSiblingMenu(e, GENDER.female)}
              disabled={
                !hasParents || !allowedActions.has(RELATIVE_ACTIONS.sister)
              }
              sx={{ display: 'flex', justifyContent: 'space-between' }}
            >
              Sister
              <ChevronRightIcon fontSize="small" sx={{ ml: 1, opacity: 0.5 }} />
            </ListItemButton>
            <ListItemButton
              onMouseEnter={(e) => {
                closeSiblingMenu();
                if (spouseIdsForPicker.length > 1)
                  openChildMenu(e, GENDER.male);
                else closeChildMenu();
              }}
              onClick={
                spouseIdsForPicker.length > 1
                  ? (e) => openChildMenu(e, GENDER.male)
                  : guarded(addChild)
              }
              disabled={uiDisabled || !allowedActions.has(RELATIVE_ACTIONS.son)}
              sx={
                spouseIdsForPicker.length > 1
                  ? { display: 'flex', justifyContent: 'space-between' }
                  : undefined
              }
            >
              Son
              {spouseIdsForPicker.length > 1 && (
                <ChevronRightIcon
                  fontSize="small"
                  sx={{ ml: 1, opacity: 0.5 }}
                />
              )}
            </ListItemButton>
            <ListItemButton
              onMouseEnter={(e) => {
                closeSiblingMenu();
                if (spouseIdsForPicker.length > 1)
                  openChildMenu(e, GENDER.female);
                else closeChildMenu();
              }}
              onClick={
                spouseIdsForPicker.length > 1
                  ? (e) => openChildMenu(e, GENDER.female)
                  : guarded(addDaughter)
              }
              disabled={
                uiDisabled || !allowedActions.has(RELATIVE_ACTIONS.daughter)
              }
              sx={
                spouseIdsForPicker.length > 1
                  ? { display: 'flex', justifyContent: 'space-between' }
                  : undefined
              }
            >
              Daughter
              {spouseIdsForPicker.length > 1 && (
                <ChevronRightIcon
                  fontSize="small"
                  sx={{ ml: 1, opacity: 0.5 }}
                />
              )}
            </ListItemButton>
            <ListItemButton
              onMouseEnter={closeSubmenus}
              onClick={guarded(addUnknownPerson)}
              disabled={
                uiDisabled || !allowedActions.has(RELATIVE_ACTIONS.unknown)
              }
            >
              Unknown
            </ListItemButton>
          </List>
        </>
      )}
      <SiblingMenu
        siblingMenuOpen={siblingMenuOpen}
        siblingAnchorEl={siblingAnchorEl}
        handleSiblingTypeSelect={guarded(addSibling)}
      />
      <ChildPartnerMenu
        childMenuOpen={childMenuOpen}
        childAnchorEl={childAnchorEl}
        partnerIds={spouseIdsForPicker}
        partnerLabel={spouseLabel}
        onSelectPartner={guarded((gender, partnerPid) => {
          closeChildMenu();
          addSingleChild(gender, partnerPid);
        })}
      />
    </Box>
  );
}

// "Son → with which partner?" nested submenu for the context-menu variant. Mirrors
// ContextMenu's own level-2 flyout Popper (state anchor + explicit high z-index + plain Grow,
// NO `transition`/function-child and NO inner ClickAwayListener) — that combination is what
// actually opens a Popper nested inside another Popper here. Earlier the missing z-index left it
// painting BEHIND the zIndex-1200 flyout, and the inner ClickAwayListener swallowed the very
// click that opened it. Closing is handled by the parent (hovering a sibling item dismisses it,
// selecting closes the whole menu, and the outer ContextMenu ClickAwayListener catches a click
// fully outside). zIndex 1300 sits above the flyout's 1200.
const ChildPartnerMenu = ({
  childMenuOpen,
  childAnchorEl,
  partnerIds,
  partnerLabel,
  onSelectPartner
}) => (
  <Popper
    open={!!childMenuOpen.open}
    anchorEl={childAnchorEl}
    placement="right-start"
    sx={{ zIndex: 1300 }}
  >
    <Grow in={!!childMenuOpen.open} style={{ transformOrigin: 'top bottom' }}>
      <Box
        sx={{
          bgcolor: 'background.paper',
          borderRadius: 1,
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: 3,
          ml: 0.5
        }}
      >
        <List>
          {partnerIds.map((partnerPid) => (
            <ListItemButton
              key={partnerPid}
              onClick={() => onSelectPartner(childMenuOpen.gender, partnerPid)}
            >
              with {partnerLabel(partnerPid)}
            </ListItemButton>
          ))}
        </List>
      </Box>
    </Grow>
  </Popper>
);

const SiblingMenu = ({
  siblingMenuOpen,
  siblingAnchorEl,
  handleSiblingTypeSelect
}) => (
  <Popper
    open={!!siblingMenuOpen.open}
    anchorEl={siblingAnchorEl}
    placement="right-start"
    sx={{ zIndex: 1300 }}
  >
    <Grow in={!!siblingMenuOpen.open} style={{ transformOrigin: 'top bottom' }}>
      <Box
        sx={{
          bgcolor: 'background.paper',
          borderRadius: 1,
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: 3,
          ml: 0.5
        }}
      >
        <List>
          <ListItemButton
            onClick={() =>
              handleSiblingTypeSelect({
                siblingType: SIBLING_TYPE.normal,
                gender: siblingMenuOpen.gender,
                twinType: TWIN_TYPE.singleton
              })
            }
          >
            Singleton
          </ListItemButton>
          <ListItemButton
            onClick={() =>
              handleSiblingTypeSelect({
                siblingType: SIBLING_TYPE.twin,
                gender: siblingMenuOpen.gender,
                twinType: TWIN_TYPE.monozygotic
              })
            }
          >
            Monozygotic
          </ListItemButton>
          <ListItemButton
            onClick={() =>
              handleSiblingTypeSelect({
                siblingType: SIBLING_TYPE.twin,
                gender: siblingMenuOpen.gender,
                twinType: TWIN_TYPE.dizygotic
              })
            }
          >
            Dizygotic
          </ListItemButton>
          <ListItemButton
            onClick={() =>
              handleSiblingTypeSelect({
                siblingType: SIBLING_TYPE.twin,
                gender: siblingMenuOpen.gender,
                twinType: TWIN_TYPE.unknown
              })
            }
          >
            Unknown
          </ListItemButton>
        </List>
      </Box>
    </Grow>
  </Popper>
);
